import { useTranslation, useUserInfo } from "@next-platform/client-common-core";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { Spinner } from "@nextui-org/react";
import { useEffect, useState } from "react";
import Countdown from "react-countdown";

import { Container, SpicyButton, Text } from "@/components/customComponents";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import Trans from "@/components/Trans/Trans";
import useApi from "@/components/useApi";
import { EXTERNAL_ROUTES_FOR_APPLICATION, PERMISSIONS_BAN_TYPES } from "@/constants";

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;

function calculateHoursFromExpiresAt(expiresAt) {
  const differenceMs = new Date(expiresAt) - new Date();
  const differenceMinutes = Math.floor(differenceMs / (MS_PER_SECOND * SECONDS_PER_MINUTE));
  return Math.ceil(differenceMinutes / MINUTES_PER_HOUR);
}

const BannedBanner = ({ banType = "" }) => {
  const applicationId = basicAppConfig.applicationId;
  const { t } = useTranslation();
  const [divStyle, setDivStyle] = useState({});
  const [image, setImage] = useState("");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  const { permissions } = useUserInfo();
  if (permissions?.includes(PERMISSIONS_BAN_TYPES.restriction_temporary)) {
    banType = PERMISSIONS_BAN_TYPES.restriction_temporary;
  } else if (permissions?.includes(PERMISSIONS_BAN_TYPES.restriction_complete)) {
    banType = PERMISSIONS_BAN_TYPES.restriction_complete;
  } else if (permissions?.includes(PERMISSIONS_BAN_TYPES.limited_access)) {
    banType = PERMISSIONS_BAN_TYPES.limited_access;
  }

  const centerStyle = "w-full h-screen flex justify-center items-center";
  const { jsonApiService, callApi, isLoading } = useApi();

  const getBanDuration = async () => {
    void callApi(jsonApiService.fetchResource, `users/moderation`).then((userModerationResponse) => {
      setExpiresAt(userModerationResponse?.expires_at);
      const hoursAbout = calculateHoursFromExpiresAt(userModerationResponse?.expires_at);
      if (userModerationResponse) {
        setDivStyle(centerStyle);
        setImage("/Assets/temp.png");
        setTitle(t("user:ban.temporary.title"));
        setContent(t("user:ban.temporary.description", { hours: hoursAbout }));
      }
    });
  };

  useEffect(() => {
    switch (banType) {
      case PERMISSIONS_BAN_TYPES.restriction_complete:
        setDivStyle(centerStyle);
        setImage("/Assets/terminated.png");
        setTitle(t("user:ban.permanent.title"));
        setContent(t("user:ban.permanent.description"));
        break;
      case PERMISSIONS_BAN_TYPES.restriction_temporary:
        // call api and get ban duration
        void getBanDuration();
        break;
      case PERMISSIONS_BAN_TYPES.limited_access:
        setDivStyle("mt-[100px]");
        setImage("/Assets/restricted.png");
        setTitle(t("user:ban.restricted.title"));
        setContent(t("user:ban.restricted.description"));
        break;
    }
  }, [banType, callApi, jsonApiService.fetchResource, permissions, t]);
  if (isLoading) {
    return <Spinner />;
  }
  if (image && title && content)
    return (
      <div className={divStyle}>
        <Container>
          <div
            className={
              `my-2 flex flex-col items-center justify-center ` +
              `${banType == PERMISSIONS_BAN_TYPES.restriction_complete || banType == PERMISSIONS_BAN_TYPES.restriction_temporary ? "p-5" : undefined}`
            }
          >
            <img src={image} alt="banned banner" height={200} width={200} />
            <Text h1>{title}</Text>
            <div className="max-w-md mb-5 [&>*:first-child]:m-0 [&>*:first-child]:text-center [&>*:first-child]:font-light ">
              <Text h6 variant={"md"}>
                {content}
              </Text>
            </div>
            {banType == PERMISSIONS_BAN_TYPES.restriction_temporary && (
              <Countdown
                date={expiresAt}
                renderer={(props) => (
                  <Text
                    h1
                  >{`${props.hours.toString().length != 2 ? "0" + props.hours : props.hours}  : ${props.minutes.toString().length != 2 ? "0" + props.minutes : props.minutes} : ${props.seconds.toString().length != 2 ? "0" + props.seconds : props.seconds} `}</Text>
                )}
              />
            )}
            <div className="flex gap-2">
              {banType === PERMISSIONS_BAN_TYPES.restriction_temporary && (
                <LocalizedLink to={EXTERNAL_ROUTES_FOR_APPLICATION[applicationId]?.guidelines} target="_blank" aria-label="guidelines">
                  <SpicyButton variant="bordered" color="secondary" className="uppercase" aria-label={t("user:ban.cta.visitGuidelines")}>
                    <Trans i18nKey="user:ban.cta.visitGuidelines" />
                  </SpicyButton>
                </LocalizedLink>
              )}
              {banType === PERMISSIONS_BAN_TYPES.limited_access && (
                <SpicyButton
                  as={LocalizedLink}
                  to="/"
                  variant="bordered"
                  color="secondary"
                  className="uppercase"
                  aria-label={t("user:ban.cta.continueToSite")}
                >
                  <Trans i18nKey="user:ban.cta.continueToSite" />
                </SpicyButton>
              )}
              <LocalizedLink to={`https://discord.gg/spicychatai`} target="_blank" aria-label="contact-us">
                <SpicyButton variant="bordered" color="secondary" className="uppercase" aria-label={t("common:contactUs")}>
                  <Trans i18nKey="common:contactUs" />
                </SpicyButton>
              </LocalizedLink>
            </div>
          </div>
        </Container>
      </div>
    );
};

export default BannedBanner;
